import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { storageApi, PickList } from '../../api/storage.api';
import { ClipboardList, Package, ArrowRight, ArrowLeft, PackagePlus, Grid3X3 } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_BADGES: Record<string, { label: string; color: string }> = {
  DRAFT: { label: 'Brouillon', color: 'bg-gray-100 text-gray-700' },
  IN_PROGRESS: { label: 'En cours', color: 'bg-blue-100 text-blue-700' },
  COMPLETED: { label: 'Terminé', color: 'bg-green-100 text-green-700' },
  CANCELLED: { label: 'Annulé', color: 'bg-red-100 text-red-700' },
};

const TYPE_LABELS: Record<string, string> = {
  ORDER: 'Commande',
  REPLENISHMENT: 'Réappro rayon',
};

export default function PickingListPage() {
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const [pickLists, setPickLists] = useState<PickList[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('active');

  useEffect(() => {
    if (tenant?.id) loadPickLists();
  }, [tenant?.id, filter]);

  const loadPickLists = async () => {
    try {
      setLoading(true);
      let data: PickList[];
      if (filter === 'active') {
        const draft = await storageApi.getPickLists('DRAFT');
        const inProgress = await storageApi.getPickLists('IN_PROGRESS');
        data = [...inProgress, ...draft];
      } else {
        data = await storageApi.getPickLists(filter === 'all' ? undefined : filter);
      }
      setPickLists(data);
    } catch {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleStartPicking = (pickListId: string) => {
    navigate(`/storage/picking/${pickListId}`);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header standalone */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors min-h-[48px]"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <ClipboardList className="w-6 h-6 text-orange-600" />
          <h1 className="text-lg font-bold text-gray-900">Picking</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/storage/putaway')}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-colors min-h-[48px]"
          >
            <PackagePlus className="w-4 h-4" />
            Rangement
          </button>
          <button
            onClick={() => navigate('/storage/locations')}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors min-h-[48px]"
          >
            <Grid3X3 className="w-4 h-4" />
            Emplacements
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
      <div className="p-6 max-w-4xl mx-auto">

      {/* Filtres */}
      <div className="flex gap-2 mb-4">
        {[
          { key: 'active', label: 'Actifs' },
          { key: 'COMPLETED', label: 'Terminés' },
          { key: 'all', label: 'Tous' },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 text-sm rounded-full ${
              filter === f.key ? 'bg-orange-100 text-orange-700 font-medium' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Liste */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Chargement...</div>
      ) : pickLists.length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-lg text-gray-500">Aucun pick list</p>
          <p className="text-sm text-gray-400 mt-1">Les pick lists sont générés depuis les commandes ou le réappro rayon</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pickLists.map(pl => {
            const badge = STATUS_BADGES[pl.status] || STATUS_BADGES.DRAFT;
            return (
              <div
                key={pl.id}
                className="bg-white rounded-lg border p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleStartPicking(pl.id)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold">{pl.pick_number}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${badge.color}`}>{badge.label}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                        {TYPE_LABELS[pl.type] || pl.type}
                      </span>
                    </div>
                    {pl.order && (
                      <p className="text-sm text-gray-500 mt-1">
                        Commande {pl.order.order_number} — {pl.order.customer_name}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(pl.created_at).toLocaleDateString('fr-BE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-lg font-bold">
                        {pl.picked_lines}/{pl.total_lines}
                      </p>
                      <p className="text-xs text-gray-400">lignes</p>
                    </div>
                    {['DRAFT', 'IN_PROGRESS'].includes(pl.status) && (
                      <ArrowRight className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>

                {/* Progress bar */}
                {pl.total_lines > 0 && (
                  <div className="mt-3 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        pl.status === 'COMPLETED' ? 'bg-green-500' : 'bg-orange-500'
                      }`}
                      style={{ width: `${(pl.picked_lines / pl.total_lines) * 100}%` }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      </div>
      </div>
    </div>
  );
}
